/**
 * This is the starting point of your application.
 * oneAudi OS and Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useContext, useEffect } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { Logger } from '@feature-hub/core';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service/dist/v2/types';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { EditorJsonContent } from './contexts/ContentInterfaces';
import { validateConfig } from '../helper/validateConfig';
import { ContentContext } from './contexts';
import mockContent from './content.json';
import FeatureGallery from './components/feature-gallery-carousel';
import { useInViewEffect } from '@oneaudi/feature-app-utils';
import { getThemeName } from '../alpha-helper';

export interface FeatureAppProps {
  trackingService?: TrackingServiceV2;
  loggerService?: Logger;

  config?: EditorJsonContent;
  environment: string;
  localeService: LocaleServiceV1;
  featureAppId: string;
}

const FeatureApp: React.FC<FeatureAppProps> = ({ config, ...inherited }: FeatureAppProps) => {
  const { localeService } = inherited;
  const context = useContext(ContentContext);

  let trackingService = {} as TrackingServiceV2;

  if (typeof window !== 'undefined' && inherited.trackingService) {
    trackingService = inherited.trackingService;
  }

  useEffect(() => {
    trackingService.featureAppName = __FEATURE_APP_NAME__;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `feature gallery - feature app ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, []);

  useInViewEffect(() => {
    const dataLayerEvent = {
      eventInfo: {
        eventAction: 'impression',
        eventName: 'feature gallery - impression',
      },
      attributes: {
        componentName: 'feature-gallery',
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        value: '',
        pos: '',
      },
    };
    trackingService.track({ event: dataLayerEvent });
  });

  return (
    <ThemeProvider
      theme={{
        name: getThemeName(context?.content?.theme === 'darkTheme' ? 'dark' : 'light'),
      }}
    >
      <FeatureGallery
        {...(validateConfig(config) ? config : context?.content || mockContent)}
        trackingService={trackingService}
        localeService={localeService}
      />
    </ThemeProvider>
  );
};
export default FeatureApp;
